"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Search, Loader2 } from "lucide-react";

export default function TrackPage() {
    const router = useRouter();
    const [refNumber, setRefNumber] = useState("");
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        if (!refNumber) {
            setError("กรุณากรอกหมายเลขอ้างอิง");
            return;
        }

        setIsLoading(true);

        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Check if reference number exists in mock data
        const validRefs = ["CMP-256901-0001", "CMP-256901-0002", "CMP-256901-0003", "CMP-256901-0004", "CMP-256901-0005", "CMP-256812-0089"];

        if (validRefs.includes(refNumber.toUpperCase())) {
            router.push(`/track/${refNumber.toUpperCase()}`);
        } else {
            setError("ไม่พบหมายเลขอ้างอิงนี้ในระบบ กรุณาตรวจสอบอีกครั้ง");
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-muted/30">
            {/* Header */}
            <header className="sticky top-0 z-50 w-full border-b bg-white">
                <div className="container mx-auto flex h-16 items-center justify-between px-4">
                    <div className="flex items-center gap-3">
                        <Image
                            src="/logo_cus.png"
                            alt="กรมศุลกากร"
                            width={48}
                            height={48}
                            className="h-12 w-12 object-contain"
                        />
                        <div className="hidden sm:block">
                            <p className="text-sm font-semibold text-primary">กรมศุลกากร</p>
                            <p className="text-xs text-muted-foreground">Thai Customs Department</p>
                        </div>
                    </div>
                    <Button variant="ghost" asChild>
                        <Link href="/">
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            กลับหน้าหลัก
                        </Link>
                    </Button>
                </div>
            </header>

            <main className="container mx-auto px-4 py-8">
                <div className="max-w-md mx-auto">
                    {/* Page Title */}
                    <div className="text-center mb-8">
                        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 mb-4">
                            <Search className="h-8 w-8 text-primary" />
                        </div>
                        <h1 className="heading-md text-primary mb-2">ติดตามสถานะเรื่องร้องเรียน</h1>
                        <p className="text-muted-foreground">กรอกหมายเลขอ้างอิงเพื่อตรวจสอบสถานะเรื่องร้องเรียนของท่าน</p>
                    </div>

                    {/* Search Form */}
                    <Card>
                        <CardHeader>
                            <CardTitle>ค้นหาเรื่องร้องเรียน</CardTitle>
                            <CardDescription>
                                กรอกหมายเลขอ้างอิงที่ได้รับเมื่อยื่นเรื่อง
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSearch} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="refNumber">หมายเลขอ้างอิง <span className="text-destructive">*</span></Label>
                                    <Input
                                        id="refNumber"
                                        placeholder="เช่น CMP-256901-0001"
                                        value={refNumber}
                                        onChange={(e) => setRefNumber(e.target.value)}
                                        className="uppercase"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">อีเมลหรือเบอร์โทรศัพท์ (ไม่บังคับ)</Label>
                                    <Input
                                        id="email"
                                        placeholder="ใช้ยืนยันตัวตน"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        สำหรับเรื่องที่ระบุข้อมูลผู้ร้อง
                                    </p>
                                </div>

                                {error && (
                                    <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-lg">
                                        {error}
                                    </div>
                                )}

                                <Button type="submit" className="w-full" disabled={isLoading}>
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            กำลังค้นหา...
                                        </>
                                    ) : (
                                        <>
                                            <Search className="mr-2 h-4 w-4" />
                                            ค้นหา
                                        </>
                                    )}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>

                    {/* Help Text */}
                    <div className="mt-6 text-center text-sm text-muted-foreground">
                        <p>หากท่านไม่มีหมายเลขอ้างอิง หรือต้องการความช่วยเหลือ</p>
                        <p>กรุณาติดต่อ สายด่วนศุลกากร <span className="font-semibold text-primary">1164</span></p>
                    </div>

                    {/* Demo Reference Numbers */}
                    <div className="mt-8 p-4 bg-muted rounded-lg">
                        <p className="text-sm font-medium mb-2">หมายเลขอ้างอิงตัวอย่าง (Demo):</p>
                        <div className="flex flex-wrap gap-2">
                            {["CMP-256901-0001", "CMP-256901-0002", "CMP-256901-0003"].map((ref) => (
                                <button
                                    key={ref}
                                    onClick={() => setRefNumber(ref)}
                                    className="text-xs px-2 py-1 bg-white rounded border hover:bg-primary hover:text-white transition-colors"
                                >
                                    {ref}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
